import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
// ECRH-4991: Self Pay flow removed. import { Elements } from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import AsyncSelect from "../../../../../../components/AsyncSelect";
import CustomDatePicker from "../../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../../components/CustomTextArea";
import CustomTimePicker from "../../../../../../components/CustomTimePicker";
import ErrorHelper from "../../../../../../components/ErrorWrapper";
import Flex from "../../../../../../components/Flex";
import Input from "../../../../../../components/Input";
import MaskMultiSelect from "../../../../../../components/MaskMultiSelect";
import MultiSelect from "../../../../../../components/MultiSelect";
import NumberInput from "../../../../../../components/NumberInput";
import PatientSelect from "../../../../../../components/PatientSelect";
import ProviderSelect from "../../../../../../components/ProviderSelect";
import StyledDialog from "../../../../../../components/StyledDialog";
import Text from "../../../../../../components/Text";
import TimeSlotPicker from "../../../../../../components/TimeSlotPicker";
import ValueSwitcher from "../../../../../../components/ValueSwitcher";
import NewPatient from "../../../../../../components/app/provider/scheduling/NewPatient";
// Recurring appointments are hidden while the functionality is unavailable.
// https://medarch.atlassian.net/browse/ECRH-6020
// import { RecurringConfig } from "../../../../../../components/app/provider/scheduling/RecurringConfig";
import { TIME_ZONE_WITH_OFFSET_LIST } from "../../../../../../constants";
import { FILLED_LOCKED_PICKER_SX } from "@/constants/scheduling";
import {
  NEW_APPOINTMENT_INSURANCE_TYPE,
  NEW_APPOINTMENT_VISIT_TYPE,
} from "../../../../../../constants/scheduling";
import { useDebounce } from "../../../../../../hooks/use-debounce";
import useDisclosure from "../../../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../../../hooks/use-reduce-disclosure";
import { useGetAllEligibilityByPatientID } from "../../../../../../queries/eligibility/use-get-all-eligibility-by-patientID";
import { useGetLatestEligibilityByInsurance } from "../../../../../../queries/eligibility/use-get-latest-eligibility-by-insurance";
import { useGetAllScreenerType } from "../../../../../../queries/patient-chart/assessment/assement-forms/use-get-all-screener-type";
import { useAppointmentTypeGetAll } from "../../../../../../queries/practice-setting/appointment-type-configuration/use-appoinment-type-get-all";
// ECRH-4991: Self Pay flow removed. import { getStripePromise } from "../../../../../../queries/stripe/use-stripe";
import { useRoleController } from "../../../../../../components/useRoleController";
import { useAddPatient } from "../../../../../../queries/use-add-patient";
import { useGetPatient } from "../../../../../../queries/use-get-patient-uuid";
import { useLocationOptionsV2 } from "../../../../../../queries/use-location-options-v2";
import { formatTime } from "../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  getXtenantId,
  replaceUnderScoreAddSpace,
  stripLeadingZeros,
} from "../../../../../../utils/utils";
import CheckEligibilityModal from "../../../patient/patient-list/patient-chart-new/demographics/eligibility/CheckEligibilityModal";
import EligibilityResponseModal from "../../../patient/patient-list/patient-chart-new/demographics/eligibility/eligibilityResponseModal";
import AddCardModal from "../../../patient/patient-list/patient-chart-new/financial/AddCardModal";
// ECRH-4991: Self Pay flow removed. import SelfPayDrawer from "../../availability/SelfPayDrawer";
import { RecurringConfig } from "@/components/app/provider/scheduling/RecurringConfig";
import { getStoredRealm } from "@/utils/auth-storage";
import {
  INTAKE_FORM_SECTIONS,
  NETWORK_STATUS,
  NETWORK_STATUS_OPTIONS,
  NewAppointmentType,
  PRIMARY_COVERAGE_FIELDS,
  REMINDER_BEFORE_UNITS,
  REMINDER_MEDIUMS,
} from "../appointment-constant";

interface NewAppointmentFormProps {
  formik: FormikProps<NewAppointmentType>;
  hideRecurring?: boolean;
  disabledPatientField?: boolean;
  hideIntakeAndAssessmentForms?: boolean;
}

export const NewAppointmentForm = ({
  formik,
  hideRecurring = false,
  disabledPatientField = false,
  hideIntakeAndAssessmentForms = false,
}: NewAppointmentFormProps) => {
  const [locationSearch, setLocationSearch] = useDebounce<string>("");
  const isTouched = useRef<boolean>(false);

  const { data: providerGroupLocationList } = useLocationOptionsV2({
    searchString: locationSearch,
    xTENANTID: getXtenantId(),
  });

  const { data: patientsData } = useGetPatient(formik.values.patientId?.value);

  const client = useQueryClient();

  useEffect(() => {
    const primary = patientsData?.patientInsurances?.filter(
      (ins: any) => ins.orderOfBenefits === "PRIMARY"
    );

    const secondary = patientsData?.patientInsurances?.filter(
      (ins: any) => ins.orderOfBenefits === "SECONDARY"
    );

    const patientTimezone = TIME_ZONE_WITH_OFFSET_LIST?.find(
      (it) => it.curType === patientsData?.timezone
    );

    formik.setFieldValue("timezone", patientTimezone);

    if (primary?.length)
      formik.setFieldValue("primary_insurance", { uuid: primary[0].uuid });
    if (secondary?.length)
      formik.setFieldValue("secondary_insurance", { uuid: secondary[0].uuid });
  }, [patientsData]);

  const { mutateAsync: addPatientApi } = useAddPatient();

  const [buttonText, setButtonText] = useState<"Add More" | "Close">("Close");
  const [openDateCalendar, setOpenDateCalendar] = useState<boolean>(true);
  const [selectedDateTime, setSelectedDateTime] = useState<dayjs.Dayjs | null>(
    dayjs(new Date())
  );

  const eligibilityResponseDisclosure = useDisclosure(false);

  // Self Pay: allow adding a patient card without leaving the schedule form.
  const addCardDisclosure = useDisclosure(false);

  // ECRH-4950: prefill the Check Eligibility modal with the patient's selected
  // primary insurance so the Insurance field auto-populates.
  const prefillEligibilityInsurance = useMemo(() => {
    const selectedInsurance = patientsData?.patientInsurances?.find(
      (ins) => ins?.uuid === formik.values.primary_insurance?.uuid
    );
    if (!selectedInsurance) return undefined;
    return {
      value: selectedInsurance?.uuid,
      label: selectedInsurance?.insurancePayer?.payerName,
      insuranceId: selectedInsurance?.insuranceId,
    };
  }, [patientsData, formik.values.primary_insurance?.uuid]);

  const prefillSecondaryEligibilityInsurance = useMemo(() => {
    const selectedInsurance = patientsData?.patientInsurances?.find(
      (ins) => ins?.uuid === formik.values.secondary_insurance?.uuid
    );
    if (!selectedInsurance) return undefined;
    return {
      value: selectedInsurance?.uuid,
      label: selectedInsurance?.insurancePayer?.payerName,
      insuranceId: selectedInsurance?.insuranceId,
    };
  }, [patientsData, formik.values.secondary_insurance?.uuid]);

  const { data: latestEligibilityData, refetch: refetchEligibility } =
    useGetAllEligibilityByPatientID({
      patientId: formik.values.patientId?.value,
      xTENANTID: getXtenantId(),
      pagination: { page: 0, pageSize: 1 },
      enabled: formik.values?.networkStatus === NETWORK_STATUS.IN_NETWORK,
    });

  const latestEligibilityRecord =
    latestEligibilityData?.data?.content?.[0] ?? null;

  const {
    data: secondaryEligibilityData,
    refetch: refetchSecondaryEligibility,
  } = useGetLatestEligibilityByInsurance({
    patientId: formik.values.patientId?.value,
    insuranceId: prefillSecondaryEligibilityInsurance?.insuranceId,
    xTENANTID: getXtenantId(),
    enabled:
      formik.values?.secondaryNetworkStatus === NETWORK_STATUS.IN_NETWORK,
  });

  const latestSecondaryEligibilityRecord =
    (secondaryEligibilityData as any)?.data?.content?.[0] ??
    (secondaryEligibilityData as any)?.data ??
    null;

  const [
    newPatientDisclosure,
    viewAvailabilityDisclosure,
    // ECRH-4991: payDisc was used for SelfPayDrawer (now removed); kept slot to preserve indices.
    _payDisc,
    checkEligibilityDisclosure,
    eligibilityReportDisclosure,
    // Secondary insurance eligibility flow (mirrors the primary disclosures above).
    checkSecondaryEligibilityDisclosure,
    secondaryEligibilityReportDisclosure,
  ] = useReduceDisclosure(7);

  // ECRH-4589: role-based access for eligibility & coverage override fields.
  // hasUpdate -> can edit/override coverage fields; hasView -> can see the
  // Check Eligibility action (hidden when the user has no permission).
  const { hasUpdate: hasUpdateEligibility, hasView: hasViewEligibility } =
    useRoleController("Check Eligibility");

  // Coverage values are captured against a specific payer, so they are cleared
  // whenever the primary insurance or its network status changes.
  function resetPrimaryCoverageFields() {
    PRIMARY_COVERAGE_FIELDS.forEach((field) =>
      formik.setFieldValue(field, undefined)
    );
  }

  function toggleDateCalendar() {
    setOpenDateCalendar(!openDateCalendar);
    openDateCalendar && viewAvailabilityDisclosure.close();
  }

  function addNewPatientHandler(values: any) {
    return addPatientApi({
      patient: values,
      xTENANTID: getStoredRealm() ?? "",
    }).then((res) => {
      client.invalidateQueries({ queryKey: ["provider-group-patients-list"] });

      return res;
    });
  }

  const handleChangeMode = (mode: string) => {
    formik.setFieldValue("paymentType", mode as "CASH" | "INSURANCE");
    // ECRH-4991: Self Pay no longer opens SelfPayDrawer on click.
    // if (mode !== "INSURANCE") {
    //   payDisc.open({ uuid: null, patientId: patientsData?.uuid });
    // }
  };

  const [searchAssessmentString, setSearchAssessmentString] =
    useDebounce<string>("");
  const { data: assessmentList } = useGetAllScreenerType({
    pagination: DEF_PAGINATION,
    xTENANTID: getXtenantId(),
    searchString: searchAssessmentString,
  });

  const autoPopulatedFormsRef = useRef<string[]>([]);

  const [searchAppointmentTypeKey, setSearchAppointmentTypeKey] =
    useDebounce<string>("");
  const {
    data: appointmentTypeList,
    isLoading: appointmentTypeDataLoading,
    isFetching: appointmentTypeFetching,
  } = useAppointmentTypeGetAll({
    // pagination: DEF_PAGINATION,
    searchString: searchAppointmentTypeKey,
    status: true,
    archive: false,
    xTENANTID: getXtenantId(),
  });
  const groupLocations = useMemo(
    () =>
      providerGroupLocationList?.data?.map((it) => ({
        value: it?.uuid,
        label: it?.name,
      })),
    [providerGroupLocationList]
  );

  return (
    <>
      {/* ECRH-4991: SelfPayDrawer removed from Schedule Appointment flow.
      <Elements stripe={getStripePromise()}>
        <SelfPayDrawer
          disclosureHost={payDisc}
          shouldPassPatientId
          selectedMode={
            formik.values.paymentType !== "INSURANCE"
              ? formik.values.paymentType
              : undefined
          }
          setSelectedPaymentMethod={(mode) => {
            formik.setFieldValue("paymentType", mode as any);
          }}
          providerId={
            formik.values.providerId?.value
              ? {
                  uuid: formik.values.providerId.value,
                  name: formik.values.providerId.label ?? "",
                }
              : undefined
          }
        />
      </Elements>
      */}

      {checkEligibilityDisclosure.opened && (
        <CheckEligibilityModal
          disclosureHost={checkEligibilityDisclosure}
          eligibilityReportDisclosure={eligibilityReportDisclosure}
          prefillIns={prefillEligibilityInsurance}
          prefillProvider={formik.values.providerId || undefined}
          onEligibilityChecked={() => {
            refetchEligibility().then((res) => {
              const record = res?.data?.data?.content?.[0];
              if (record) {
                eligibilityResponseDisclosure.open(record);
              }
            });
          }}
        />
      )}
      {checkSecondaryEligibilityDisclosure.opened && (
        <CheckEligibilityModal
          disclosureHost={checkSecondaryEligibilityDisclosure}
          eligibilityReportDisclosure={secondaryEligibilityReportDisclosure}
          prefillIns={prefillSecondaryEligibilityInsurance}
          prefillProvider={formik.values.providerId || undefined}
          onEligibilityChecked={() => {
            refetchSecondaryEligibility().then((res) => {
              const record =
                (res?.data as any)?.data?.content?.[0] ??
                (res?.data as any)?.data;
              if (record) {
                eligibilityResponseDisclosure.open(record);
              }
            });
          }}
        />
      )}
      {eligibilityResponseDisclosure.opened && (
        <EligibilityResponseModal
          disclosureHost={eligibilityResponseDisclosure}
        />
      )}

      {addCardDisclosure.opened && (
        <AddCardModal
          disclosureHost={addCardDisclosure}
          patientId={formik.values.patientId?.value}
        />
      )}

      <NewPatient
        open={newPatientDisclosure.opened}
        onClose={newPatientDisclosure.close}
        onSubmit={addNewPatientHandler}
      />
      <Flex width={"100%"} flexDirection={"column"}>
        <Grid container spacing={2}>
          <Grid size={5}>
            <PatientSelect
              disabled={disabledPatientField}
              handleBlur={formik.handleBlur}
              showEmail={false}
              // onSearch={setPatientSearch}
              label="Patient Name"
              placeholder=""
              required
              name="patientId"
              // Todo: need to remove once confirm with the feature of patientSelect
              // renderCustomLabel={(option) => (
              //   <Flex flexDirection={"column"}>
              //     <Text>{option.label}</Text>
              //     <Text>
              //       {option?.birthDate ? formatDate(option?.birthDate) : ""}
              //     </Text>
              //   </Flex>
              // )}
              // options={
              //   patientList?.data?.content?.map((p) => {
              //     return {
              //       value: p.uuid ?? "",
              //       label: `${p.firstName}  ${p.lastName}`,
              //       // birthDate: p?.birthDate ?? "",
              //     };
              //   }) ?? []
              // }
              value={formik.values.patientId ?? null}
              onChange={(value) => {
                formik.setFieldValue("patientId", value);
              }}
              error={formik.touched?.patientId && formik.errors?.patientId}
            />
          </Grid>
          <Grid
            sx={{
              alignItems: "center",
            }}
            size={1}
          >
            {/* Add New Patient is hidden inside patient charting, where the
                patient context already exists (patient field pre-filled). */}
            {!disabledPatientField && (
              <Tooltip title={"Add New Patient"}>
                <PersonAddIcon
                  sx={{ cursor: "pointer", color: "primary.main", mt: 0.5 }}
                  onClick={newPatientDisclosure.open}
                />
              </Tooltip>
            )}
          </Grid>
          <Grid size={6}>
            <AsyncSelect
              required
              loading={appointmentTypeDataLoading || appointmentTypeFetching}
              error={formik.touched?.type && formik.errors?.type}
              name="type"
              handleBlur={formik.handleBlur}
              label="Appointment Type"
              onSearch={setSearchAppointmentTypeKey}
              options={appointmentTypeList?.data?.content?.map((it: any) => ({
                value: it?.uuid,
                label: it?.nameString,
                duration: it?.duration,
                intakeForms: it?.intakeForms,
              }))}
              value={
                formik.values.type && typeof formik.values.type === "object"
                  ? formik.values.type
                  : formik.values.type
                    ? {
                        value: formik.values.type,
                        label: formik.values.type,
                      }
                    : null
              }
              onChange={(value: any) => {
                formik.setFieldValue("type", value).then(() => {
                  formik.validateField("type");
                });

                // Remove only previously auto-populated forms, keep manually selected ones
                const currentForms = (formik.values.customForms ?? []) as any[];
                const manualForms = currentForms.filter(
                  (f: any) => !autoPopulatedFormsRef.current.includes(f.value)
                );

                if (value?.intakeForms) {
                  const formIds = value.intakeForms
                    .split(",")
                    .map((s: string) => s.trim());
                  const matchedForms = INTAKE_FORM_SECTIONS.filter((f) =>
                    formIds.includes(f.value)
                  );
                  autoPopulatedFormsRef.current = matchedForms.map(
                    (f) => f.value
                  );

                  // Merge: manual forms + new auto forms (no duplicates)
                  const merged = [
                    ...manualForms,
                    ...matchedForms.filter(
                      (f) => !manualForms.some((m: any) => m.value === f.value)
                    ),
                  ];
                  formik.setFieldValue("customForms", merged);
                  formik.setFieldValue("sendForms", true);
                  setButtonText("Add More");
                } else {
                  autoPopulatedFormsRef.current = [];
                  formik.setFieldValue(
                    "customForms",
                    manualForms.length > 0 ? manualForms : undefined
                  );
                }
              }}
            />
          </Grid>
          <Grid size={12}>
            <Input
              textFieldProps={{
                name: "chiefComplaint",
                onBlur: formik.handleBlur,
                value: formik.values.chiefComplaint,
                onChange: (e) => {
                  formik.setFieldValue("chiefComplaint", e.currentTarget.value);
                },
              }}
              label="Chief Complaint"
              required
              error={
                formik.touched?.chiefComplaint &&
                (formik.errors?.chiefComplaint as any)
              }
            />
          </Grid>

          <Grid size={4}>
            <AsyncSelect
              required
              name="timezone"
              handleBlur={formik.handleBlur}
              error={
                formik.touched?.timezone && (formik.errors?.timezone as any)
              }
              label="Timezone"
              options={TIME_ZONE_WITH_OFFSET_LIST}
              value={formik.values?.timezone}
              onChange={(value) => {
                formik.setFieldValue("timezone", value);
              }}
            />
          </Grid>

          <Grid size={4}>
            <Flex alignItems={"center"} gap={1}>
              <Text>Visit Type:</Text>
              <ValueSwitcher
                alignment={formik.values.mode}
                handleChange={(_, value) => {
                  formik.setFieldValue(
                    "mode",
                    value as "IN_PERSON" | "VIRTUAL"
                  );
                  formik.setFieldValue("locationId", undefined);
                  // Availability differs by visit type (and location is cleared),
                  // so any previously picked slot is no longer valid.
                  formik.setFieldValue("startTime", undefined);
                  formik.setFieldValue("endTime", undefined);
                  formik.setFieldValue("duration", undefined);
                }}
                timing={NEW_APPOINTMENT_VISIT_TYPE}
              />
            </Flex>
          </Grid>
          {/* Location is available for both visit types: mandatory for
              In-Person, optional for Telehealth (a Telehealth booking with a
              location also picks up that location's In-Person availability). */}
          <Grid size={4}>
            <AsyncSelect
              required={formik.values.mode === "IN_PERSON"}
              label="Location"
              options={groupLocations}
              onSearch={setLocationSearch}
              value={formik.values.locationId}
              onChange={(opt) => {
                if (!opt) formik.setFieldValue("providerId", undefined);

                formik.setFieldValue("locationId", opt);
                // Availability is location-specific; drop any slot picked for
                // the previous location.
                formik.setFieldValue("startTime", undefined);
                formik.setFieldValue("endTime", undefined);
                formik.setFieldValue("duration", undefined);
              }}
              name="locationId"
              handleBlur={formik.handleBlur}
              error={formik.touched?.locationId && formik.errors?.locationId}
            />
          </Grid>
          <Grid size={9}>
            <ProviderSelect
              required
              label="Provider"
              error={
                formik.touched.providerId && (formik.errors.providerId as any)
              }
              value={formik.values?.providerId || null}
              onChange={(value) => formik.setFieldValue("providerId", value)}
            />
          </Grid>
          <Grid size={3}>
            {/* ErrorHelper's `width: inherit` re-applies the Grid cell's calc width inside the cell. */}
            <Box sx={{ width: "100%" }}>
              <ErrorHelper
                error={
                  (isTouched.current || formik.submitCount > 0) &&
                  (formik.errors?.duration as string)
                }
              >
                {/* In-Person slots must not be shown until a location is chosen. */}
                <Tooltip
                  title={
                    formik.values.mode === "IN_PERSON" &&
                    !formik.values.locationId
                      ? "Please select a location first"
                      : ""
                  }
                >
                  <Box component="span" sx={{ display: "block" }}>
                    <Button
                      disabled={
                        !formik.values.providerId ||
                        !formik.values.type ||
                        (formik.values.mode === "IN_PERSON" &&
                          !formik.values.locationId)
                      }
                      sx={{
                        height: "34px",
                        borderColor:
                          !formik.values?.duration && isTouched.current
                            ? "red"
                            : "primary.main",
                      }}
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        viewAvailabilityDisclosure.open();
                        setOpenDateCalendar(true);
                        isTouched.current = true;
                      }}
                    >
                      View availability
                    </Button>
                  </Box>
                </Tooltip>
              </ErrorHelper>
            </Box>
          </Grid>
          <Grid size={4}>
            <CustomDatePicker
              disabled
              flexprops={{
                sx: formik.values?.duration
                  ? FILLED_LOCKED_PICKER_SX
                  : undefined,
              }}
              onChange={() => {}}
              label="Date"
              value={formik.values?.duration ? selectedDateTime : undefined}
              // onChange={(value) => setSelectedDateTime(value)}
            />
          </Grid>
          <Grid size={4}>
            <CustomTimePicker
              valueFormatter="hh:mm A"
              disabled
              flexProps={{
                sx:
                  formik?.values?.startTime && formik.values?.duration
                    ? FILLED_LOCKED_PICKER_SX
                    : undefined,
              }}
              label="Time"
              value={
                formik?.values?.startTime && formik.values?.duration
                  ? formatTime(new Date(formik.values?.startTime), {
                      // withSeconds: true,
                      timezone: formik.values?.timezone?.value ?? undefined,
                    })
                  : undefined
              }
              // onChange={(value) => setTempTime(value)}
            />
          </Grid>

          <Grid size={4}>
            <CustomSelect
              readOnly
              label="Duration"
              value={formik.values?.duration}
              // onChange={(value) => setTempDuration(value)}
              options={
                formik.values?.duration
                  ? [
                      {
                        value: formik.values?.duration,
                        label: formik.values?.duration,
                      },
                    ]
                  : undefined
              }
            />
          </Grid>

          {/* In the Visit type field, if the user has selected 'In Person' visit type, then only this field will be displayed */}
          <StyledDialog
            open={viewAvailabilityDisclosure.opened}
            onClose={viewAvailabilityDisclosure.close}
          >
            <Grid size={12}>
              <Box
                sx={{
                  width: "45rem",
                }}
              >
                <TimeSlotPicker
                  isTouched={isTouched}
                  formik={formik}
                  appointmentType={
                    typeof formik.values.type === "object" &&
                    (formik.values.type as any)?.value
                      ? (formik.values.type as any).value
                      : (formik.values.type as any)
                  }
                  mode={formik.values.mode}
                  providerUuid={formik.values.providerId?.value ?? ""}
                  selectedLocation={formik.values.locationId?.value}
                  // label="Date & Time"
                  onChange={(value) => {
                    setSelectedDateTime(value as dayjs.Dayjs);

                    if (
                      formik.values?.endDate &&
                      dayjs(formik?.values?.startTime).isAfter(
                        dayjs(formik?.values?.endDate)
                      )
                    ) {
                      formik.setFieldValue("endDate", null);
                    }
                  }}
                  selectedDateTime={selectedDateTime}
                  openDateCalendar={openDateCalendar}
                  toggleDateCalendar={toggleDateCalendar}
                  type="new"
                />
              </Box>
            </Grid>
          </StyledDialog>

          {!hideRecurring && (
            <>
              {/* Recurring appointments are hidden while the functionality is
                  unavailable. Set Reminder below stays active.
                  https://medarch.atlassian.net/browse/ECRH-6020 */}
              <Grid
                sx={{
                  alignItems: "start",
                }}
                size={{
                  sm: 12,
                  md: 6,
                  lg: 6,
                }}
              >
                <FormControlLabel
                  name="isRecurring"
                  control={<Checkbox />}
                  label={<Text fontWeight={500}> Recurring</Text>}
                  checked={formik.values.isRecurring}
                  onChange={formik.handleChange}
                />

                {formik.values.isRecurring && (
                  <RecurringConfig formik={formik} required />
                )}
              </Grid>

              <Grid
                rowSpacing={1}
                size={{
                  sm: 12,
                  md: 6,
                  lg: 6,
                }}
              >
                <FormControlLabel
                  name="reminder_set"
                  control={<Checkbox />}
                  label={<Text fontWeight={500}>Set Reminder</Text>}
                  checked={formik.values.reminder_set}
                  onChange={() => {
                    formik.setFieldValue(
                      "reminder_set",
                      !formik.values.reminder_set
                    );
                    formik.setFieldValue("reminders", undefined);
                  }}
                />

                {formik.values.reminder_set && (
                  <Grid container spacing={2} size={12}>
                    <Grid
                      size={{
                        sm: 12,
                      }}
                    >
                      <MultiSelect
                        required
                        label="Reminder Mediums"
                        name="reminders.reminderMedium"
                        handleBlur={formik.handleBlur}
                        options={REMINDER_MEDIUMS}
                        value={formik.values.reminders?.reminderMedium}
                        onChange={(values) => {
                          formik.setFieldValue(
                            "reminders.reminderMedium",
                            values
                          );
                        }}
                        error={
                          formik.touched.reminder_set &&
                          (formik.errors.reminders as any)?.reminderMedium
                        }
                      />
                    </Grid>
                    <Grid size={4}>
                      <NumberInput
                        required
                        textFieldProps={{
                          name: "reminders.reminderBeforeNumber",
                          onBlur: formik.handleBlur,
                          // type: "number",
                          inputProps: { min: 1 },
                          value: formik.values.reminders?.reminderBeforeNumber,
                          onChange: (e) => {
                            formik.setFieldValue(
                              "reminders.reminderBeforeNumber",
                              e.currentTarget.value
                            );
                          },
                        }}
                        label="Remind Before"
                        error={
                          formik.touched.reminder_set &&
                          (formik.errors.reminders as any)?.reminderBeforeNumber
                        }
                      />
                    </Grid>
                    <Grid size={8}>
                      <CustomSelect
                        required
                        label=""
                        name="reminders.reminderBeforeUnit"
                        handleBlur={formik.handleBlur}
                        options={REMINDER_BEFORE_UNITS}
                        clearable={false}
                        value={formik.values.reminders?.reminderBeforeUnit}
                        onChange={(value) => {
                          formik.setFieldValue(
                            "reminders.reminderBeforeUnit",
                            value
                          );
                        }}
                        error={
                          formik.touched.reminder_set &&
                          (formik.errors.reminders as any)?.reminderBeforeUnit
                        }
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Flex alignItems={"center"} gap={1}>
                    <Text fontWeight={500}>Payment Mode:</Text>
                    <ValueSwitcher
                      alignment={
                        formik.values?.paymentType !== "INSURANCE"
                          ? "ONLINE"
                          : formik.values?.paymentType
                      }
                      handleChange={(_, newAlignment) =>
                        handleChangeMode(newAlignment)
                      }
                      timing={NEW_APPOINTMENT_INSURANCE_TYPE}
                    />
                    {formik.values?.paymentType !== "INSURANCE" && (
                      <Tooltip
                        title={
                          !formik.values.patientId?.value
                            ? "Select a patient first"
                            : ""
                        }
                      >
                        <Box component="span">
                          <Text
                            sx={{
                              color: "primary.main",
                              cursor: !formik.values.patientId?.value
                                ? "not-allowed"
                                : "pointer",
                              textDecoration: "underline",
                              fontWeight: 500,
                              opacity: !formik.values.patientId?.value
                                ? 0.5
                                : 1,
                            }}
                            onClick={() => {
                              if (formik.values.patientId?.value) {
                                addCardDisclosure.open();
                              }
                            }}
                          >
                            Add Card Details
                          </Text>
                        </Box>
                      </Tooltip>
                    )}
                  </Flex>
                </Grid>
                {formik.values?.paymentType === "INSURANCE" && (
                  <Grid size={6}>
                    <CustomSelect
                      readOnly={!formik.values.patientId}
                      label="Primary Insurance"
                      options={patientsData?.patientInsurances?.map(
                        (fIns: any) => {
                          return {
                            value: fIns?.uuid,
                            label: fIns?.insurancePayer?.payerName,
                          };
                        }
                      )}
                      value={formik.values.primary_insurance?.uuid}
                      onChange={(value) => {
                        const selectedIns =
                          patientsData?.patientInsurances?.findIndex(
                            (it: any) => it?.uuid === value
                          );

                        formik.setFieldValue(
                          "primary_insurance",
                          selectedIns !== undefined
                            ? {
                                uuid: patientsData?.patientInsurances?.[
                                  selectedIns
                                ]?.uuid,
                              }
                            : null
                        );

                        // Network status is only meaningful for the selected
                        // insurance, so switching or clearing it starts over.
                        formik.setFieldValue("networkStatus", undefined);
                        resetPrimaryCoverageFields();
                      }}
                    />
                  </Grid>
                )}

                {formik.values?.paymentType === "INSURANCE" && (
                  <Grid size={6}>
                    <CustomSelect
                      readOnly={!formik.values.patientId}
                      label="Secondary Insurance"
                      options={patientsData?.patientInsurances?.map(
                        (fIns: any) => {
                          return {
                            value: fIns?.uuid,
                            label: fIns?.insurancePayer?.payerName,
                          };
                        }
                      )}
                      value={formik.values.secondary_insurance?.uuid}
                      onChange={(value) => {
                        const selectedIns =
                          patientsData?.patientInsurances?.findIndex(
                            (it: any) => it?.uuid === value
                          );

                        formik.setFieldValue(
                          "secondary_insurance.uuid",
                          selectedIns !== undefined
                            ? patientsData?.patientInsurances?.[selectedIns]
                                ?.uuid
                            : null
                        );

                        formik.setFieldValue(
                          "secondaryNetworkStatus",
                          undefined
                        );
                      }}
                      error={
                        // (formik.touched.secondary_insurance as any)?.uuid &&
                        (formik.errors.secondary_insurance as any)?.uuid
                      }
                    />
                  </Grid>
                )}

                {formik.values?.paymentType === "INSURANCE" && (
                  <>
                    <Grid size={12}>
                      <Flex gap={2} flexWrap="wrap" alignItems="flex-start">
                        {/* Primary insurance: independent network status +
                        eligibility. Both only make sense once a primary
                        insurance is selected. */}
                        {!!formik.values.primary_insurance?.uuid && (
                          <Flex gap={3} flexWrap="wrap" alignItems="flex-start">
                            {hasUpdateEligibility && (
                              <Box sx={{ minWidth: 220 }}>
                                <CustomSelect
                                  label="Primary Network Status"
                                  value={formik.values.networkStatus}
                                  options={NETWORK_STATUS_OPTIONS}
                                  onChange={(value) => {
                                    formik.setFieldValue(
                                      "networkStatus",
                                      value
                                    );
                                    resetPrimaryCoverageFields();
                                  }}
                                />
                              </Box>
                            )}
                            {formik.values.networkStatus ===
                              NETWORK_STATUS.IN_NETWORK && (
                              /* Primary insurance eligibility */
                              <Box>
                                <Text>Primary Insurance Eligibility</Text>
                                <Flex alignItems="center" gap={1.5} mt={0.5}>
                                  {latestEligibilityRecord ? (
                                    <>
                                      <Chip
                                        label="Verified"
                                        size="small"
                                        sx={{
                                          bgcolor: "#e8f5e9",
                                          color: "#2e7d32",
                                        }}
                                      />
                                      <Text
                                        sx={{
                                          color: "primary.main",
                                          cursor: "pointer",
                                          textDecoration: "underline",
                                        }}
                                        onClick={() =>
                                          eligibilityResponseDisclosure.open(
                                            latestEligibilityRecord
                                          )
                                        }
                                      >
                                        View Report
                                      </Text>
                                      {hasViewEligibility && (
                                        <Flex
                                          alignItems="center"
                                          gap={0.5}
                                          sx={{ cursor: "pointer" }}
                                          onClick={() =>
                                            checkEligibilityDisclosure.open(
                                              formik.values.patientId?.value
                                            )
                                          }
                                        >
                                          <RefreshOutlinedIcon
                                            sx={{
                                              fontSize: 14,
                                              color: "primary.main",
                                            }}
                                          />
                                          <Text
                                            sx={{
                                              color: "primary.main",
                                              textDecoration: "underline",
                                              fontWeight: 500,
                                            }}
                                          >
                                            Recheck
                                          </Text>
                                        </Flex>
                                      )}
                                    </>
                                  ) : (
                                    hasViewEligibility && (
                                      <Button
                                        variant="text"
                                        color="primary"
                                        disabled={
                                          !formik.values.primary_insurance?.uuid
                                        }
                                        onClick={() =>
                                          checkEligibilityDisclosure.open(
                                            formik.values.patientId?.value
                                          )
                                        }
                                        sx={{
                                          textTransform: "none",
                                          minWidth: 0,
                                          p: 0,
                                        }}
                                      >
                                        Check Eligibility
                                      </Button>
                                    )
                                  )}
                                </Flex>
                              </Box>
                            )}
                          </Flex>
                        )}

                        {/* Secondary insurance: independent network status + eligibility */}
                        {formik.values.secondary_insurance?.uuid && (
                          <Flex gap={3} flexWrap="wrap" alignItems="flex-start">
                            {hasUpdateEligibility && (
                              <Box sx={{ minWidth: 220 }}>
                                <CustomSelect
                                  label="Secondary Network Status"
                                  value={formik.values.secondaryNetworkStatus}
                                  options={NETWORK_STATUS_OPTIONS}
                                  onChange={(value) => {
                                    formik.setFieldValue(
                                      "secondaryNetworkStatus",
                                      value
                                    );
                                  }}
                                />
                              </Box>
                            )}
                            {formik.values.secondaryNetworkStatus ===
                              NETWORK_STATUS.IN_NETWORK && (
                              <Box>
                                <Text>Secondary Insurance Eligibility</Text>
                                <Flex alignItems="center" gap={1.5} mt={0.5}>
                                  {latestSecondaryEligibilityRecord ? (
                                    <>
                                      <Chip
                                        label="Verified"
                                        size="small"
                                        sx={{
                                          bgcolor: "#e8f5e9",
                                          color: "#2e7d32",
                                        }}
                                      />
                                      <Text
                                        sx={{
                                          color: "primary.main",
                                          cursor: "pointer",
                                          textDecoration: "underline",
                                        }}
                                        onClick={() =>
                                          eligibilityResponseDisclosure.open(
                                            latestSecondaryEligibilityRecord
                                          )
                                        }
                                      >
                                        View Report
                                      </Text>
                                      {hasViewEligibility && (
                                        <Flex
                                          alignItems="center"
                                          gap={0.5}
                                          sx={{ cursor: "pointer" }}
                                          onClick={() =>
                                            checkSecondaryEligibilityDisclosure.open(
                                              formik.values.patientId?.value
                                            )
                                          }
                                        >
                                          <RefreshOutlinedIcon
                                            sx={{
                                              fontSize: 14,
                                              color: "primary.main",
                                            }}
                                          />
                                          <Text
                                            sx={{
                                              color: "primary.main",
                                              textDecoration: "underline",
                                              fontWeight: 500,
                                            }}
                                          >
                                            Recheck
                                          </Text>
                                        </Flex>
                                      )}
                                    </>
                                  ) : (
                                    hasViewEligibility && (
                                      <Button
                                        variant="text"
                                        color="primary"
                                        disabled={
                                          !formik.values.secondary_insurance
                                            ?.uuid
                                        }
                                        onClick={() =>
                                          checkSecondaryEligibilityDisclosure.open(
                                            formik.values.patientId?.value
                                          )
                                        }
                                        sx={{
                                          textTransform: "none",
                                          minWidth: 0,
                                          p: 0,
                                        }}
                                      >
                                        Check Eligibility
                                      </Button>
                                    )
                                  )}
                                </Flex>
                              </Box>
                            )}
                          </Flex>
                        )}
                      </Flex>
                    </Grid>

                    {formik.values.networkStatus ===
                      NETWORK_STATUS.OUT_OF_NETWORK &&
                      hasUpdateEligibility && (
                        <>
                          <Grid size={12}>
                            <Typography
                              sx={{
                                fontWeight: 500,
                              }}
                            >
                              Coverage Details
                            </Typography>
                          </Grid>
                          <Grid size={3}>
                            <NumberInput
                              label="Copay Amount"
                              textFieldProps={{
                                name: "copayAmount",
                                value: formik.values.copayAmount,
                                onChange: (e) => {
                                  e.target.value = stripLeadingZeros(
                                    e.target.value
                                  );
                                  formik.handleChange(e);
                                },
                                onBlur: formik.handleBlur,
                                // Currency symbol so the copay input matches how
                                // amounts are rendered everywhere else.
                                InputProps: {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      $
                                    </InputAdornment>
                                  ),
                                },
                              }}
                            />
                          </Grid>
                          <Grid size={3}>
                            <NumberInput
                              label="Co-Insurance %"
                              max={100}
                              textFieldProps={{
                                name: "coInsuranceAmount",
                                value: formik.values.coInsuranceAmount,
                                onChange: (e) => {
                                  e.target.value = stripLeadingZeros(
                                    e.target.value
                                  );
                                  formik.handleChange(e);
                                },
                                onBlur: formik.handleBlur,
                                InputProps: {
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton>
                                        <PercentIcon
                                          sx={{ height: "1.2rem" }}
                                        />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                },
                              }}
                            />
                          </Grid>
                          <Grid size={3}>
                            <NumberInput
                              label="Deductible Amount"
                              textFieldProps={{
                                name: "deductible",
                                value: formik.values.deductible,
                                onChange: (e) => {
                                  e.target.value = stripLeadingZeros(
                                    e.target.value
                                  );
                                  formik.handleChange(e);
                                },
                                onBlur: formik.handleBlur,
                              }}
                            />
                          </Grid>
                          <Grid size={3}>
                            <Input
                              label="Coverage Level"
                              textFieldProps={{
                                name: "coverageLevel",
                                value: formik.values.coverageLevel,
                                onChange: formik.handleChange,
                                onBlur: formik.handleBlur,
                              }}
                            />
                          </Grid>
                          <Grid size={3}>
                            <CustomSelect
                              label="Verification Status"
                              name="verificationStatus"
                              handleBlur={formik.handleBlur}
                              options={[
                                { value: "DONE", label: "Done" },
                                { value: "PENDING", label: "Pending" },
                              ]}
                              value={formik.values.verificationStatus}
                              onChange={(value) => {
                                formik.setFieldValue(
                                  "verificationStatus",
                                  value
                                );
                              }}
                            />
                          </Grid>
                          <Grid size={6}>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 0.5,
                              }}
                            >
                              Pre-Authorization Required?
                            </Typography>
                            <RadioGroup
                              row
                              name="priorAuthorization"
                              value={
                                formik.values.priorAuthorization === true
                                  ? "true"
                                  : formik.values.priorAuthorization === false
                                    ? "false"
                                    : ""
                              }
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "priorAuthorization",
                                  e.target.value === "true"
                                )
                              }
                            >
                              <FormControlLabel
                                value="true"
                                control={<Radio size="small" />}
                                label="Yes"
                              />
                              <FormControlLabel
                                value="false"
                                control={<Radio size="small" />}
                                label="No"
                              />
                            </RadioGroup>
                          </Grid>
                        </>
                      )}
                  </>
                )}
                <Grid size={12}>
                  <CustomTextArea
                    minRows={3}
                    label="Patient Appointment Note"
                    onChange={formik.handleChange}
                    name={"note"}
                    value={formik.values.note}
                  />
                </Grid>
                {!hideIntakeAndAssessmentForms && (
                  <Grid size={10}>
                    <Flex alignItems={"center"}>
                      <FormControlLabel
                        control={<Checkbox />}
                        label={
                          <Text fontWeight={500}>
                            <DescriptionOutlinedIcon />
                            Forms will be emailed
                          </Text>
                        }
                        checked={buttonText === "Add More"}
                        onChange={(_, v) => {
                          if (!v) {
                            formik.setFieldValue("assessments", undefined);
                            formik.setFieldValue("customForms", undefined);
                            autoPopulatedFormsRef.current = [];
                          }
                          formik.setFieldValue("sendForms", v);

                          setButtonText((prev) =>
                            prev === "Add More" ? "Close" : "Add More"
                          );
                        }}
                      />
                    </Flex>
                  </Grid>
                )}

                {buttonText === "Add More" && (
                  <Grid
                    container
                    sx={{
                      alignContent: "flex-start",
                    }}
                    size={6}
                  >
                    <Grid size={12}>
                      <MaskMultiSelect
                        label="Intake Forms"
                        options={INTAKE_FORM_SECTIONS}
                        optionWithCheckbox={true}
                        value={formik.values.customForms as any}
                        onChange={(values) => {
                          formik.setFieldValue("customForms", values);
                        }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <ul
                        style={{
                          listStyleType: "square",
                        }}
                      >
                        {(formik.values?.customForms ?? [])?.map((f: any) => (
                          <li key={f?.value}>
                            <Text color={"grey"}>{f?.label}</Text>
                          </li>
                        ))}
                      </ul>
                    </Grid>
                  </Grid>
                )}
                {buttonText === "Add More" && (
                  <Grid
                    container
                    sx={{
                      alignContent: "flex-start",
                    }}
                    size={6}
                  >
                    <Grid size={12}>
                      {buttonText === "Add More" && (
                        <MaskMultiSelect
                          onSearch={(value) => setSearchAssessmentString(value)}
                          label="Assessments"
                          options={
                            assessmentList?.data?.content?.map((item: any) => {
                              return {
                                value: item,
                                label: replaceUnderScoreAddSpace(item),
                              };
                            }) ?? []
                          }
                          optionWithCheckbox={true}
                          value={formik.values.assessments as any}
                          onChange={(values) =>
                            formik.setFieldValue("assessments", values)
                          }
                        />
                      )}
                    </Grid>
                    <Grid size={12}>
                      <ul
                        style={{
                          listStyleType: "square",
                        }}
                      >
                        {(formik.values.assessments as any)?.map((f: any) => (
                          <li>
                            <Text color={"grey"}>{f?.label}</Text>
                          </li>
                        ))}
                      </ul>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Flex>
    </>
  );
};
